import {
  Box,
  Typography,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import useRoutines from "../hooks/use-routines";
import useAvailableRoutines from "../hooks/use-available-routines";
import { useRoutineManagement } from "../hooks/use-routine-management";
import RoutinesTable from "./routines-table";
import RoutineFormDialog from "./routine-form-dialog";
import RoutineDeleteDialog from "./routine-delete-dialog";

interface TemplateRoutinesProps {
  templateId: number;
}

const TemplateRoutines = ({ templateId }: TemplateRoutinesProps) => {
  const { routines, isLoading, mutate } = useRoutines(templateId);
  const { availableRoutines, isLoading: isLoadingAvailable } =
    useAvailableRoutines();

  const {
    openDialog,
    selectedRoutineId,
    isSaving,
    setSelectedRoutineId,
    handleOpenDialog,
    handleCloseDialog,
    handleSave,
    getAvailableOptions,
    deleteConfirmOpen,
    routineToDelete,
    isDeleting,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  } = useRoutineManagement({
    templateId,
    routines: routines ?? [],
    availableRoutines,
    mutate,
  });

  if (isLoading || isLoadingAvailable) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 3,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" fontWeight={600}>
              Routines
            </Typography>
            <Chip
              label={`${routines?.length ?? 0} active`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          </Box>
          <IconButton
            size="small"
            color="success"
            onClick={handleOpenDialog}
            aria-label="Add new routine"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />

        <Box
          sx={{
            border: 1,
            borderColor: "grey.300",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <RoutinesTable routines={routines ?? []} onDelete={handleDelete} />
        </Box>
      </Box>

      <RoutineFormDialog
        open={openDialog}
        selectedRoutineId={selectedRoutineId}
        availableOptions={getAvailableOptions()}
        allAvailableRoutines={availableRoutines}
        isSaving={isSaving}
        onClose={handleCloseDialog}
        onSave={handleSave}
        onRoutineChange={setSelectedRoutineId}
      />

      <RoutineDeleteDialog
        open={deleteConfirmOpen}
        routine={routineToDelete}
        isDeleting={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default TemplateRoutines;
